import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { api, formatApiError } from "@/lib/api";
import { claimTypesFor, coverageLabel } from "@/lib/constants";

const EMPTY = { incident_date: "", incident_type: "", location: "", description: "", police_report: "", other_party: "", injuries: false };

const Pick = ({ label, value, onChange, items, testId }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Select…" /></SelectTrigger>
      <SelectContent>{items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const ClaimDialog = ({ policies, onDone, trigger }) => {
  const today = new Date().toLocaleDateString("en-CA");
  const active = policies.filter((p) => (p.status === "active" || p.cancelled_at) && p.effective_date <= today);
  const [open, setOpen] = useState(false);
  const [policyId, setPolicyId] = useState(active.length === 1 ? active[0].id : "");
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [filed, setFiled] = useState(null);
  const pol = active.find((p) => p.id === policyId);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const ok = pol && f.incident_date && f.incident_type && f.location.length >= 2 && f.description.length >= 10;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/claims", { policy_id: policyId, ...f });
      toast.success(`Claim ${data.claim_number} filed`);
      setFiled(data);
      setF(EMPTY);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setFiled(null); }}>
      <DialogTrigger asChild>{trigger || <Button variant="outline" disabled={!active.length} data-testid="file-claim-btn"><AlertTriangle className="mr-2 h-4 w-4" /> File a claim</Button>}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle>File a claim</DialogTitle>
          <DialogDescription>{filed ? "Your claim is in. Our claims team will review it and post updates in your account." : "Tell us what happened. You can add photos or a police report right after you submit."}</DialogDescription>
        </DialogHeader>
        {filed ? (
          <div className="grid gap-4" data-testid="claim-filed-step">
            <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">Claim <b>{filed.claim_number}</b> filed. Add photos of the damage or a police report now (optional).</p>
            <DocumentsPanel policyId={filed.policy_id} claimId={filed.id} defaultType="claim_photo" compact />
            <Button onClick={() => { setOpen(false); setFiled(null); }} data-testid="claim-done-btn">Done</Button>
          </div>
        ) : (
        <div className="grid gap-4">
          <Pick label="Policy" value={policyId} onChange={(v) => { setPolicyId(v); setF({ ...f, incident_type: "" }); }} testId="claim-policy-select"
            items={active.map((p) => ({ value: p.id, label: `${p.policy_number} · ${coverageLabel(p.coverage_type)}` }))} />
          {pol && pol.vehicle && <p className="rounded-md bg-slate-50 px-3 py-2 text-sm" data-testid="claim-vehicle-info">Vehicle on policy: <b>{pol.vehicle}</b>{pol.vin_last6 && <> · VIN …<span className="font-mono">{pol.vin_last6}</span></>}</p>}
          {pol && <Pick label="What happened?" value={f.incident_type} onChange={set("incident_type")} items={claimTypesFor(pol.coverage_type)} testId="claim-type-select" />}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="c-date">Date of incident</Label><Input id="c-date" type="date" max={new Date().toLocaleDateString("en-CA")} value={f.incident_date} onChange={set("incident_date")} data-testid="claim-date-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-loc">Location</Label><Input id="c-loc" placeholder="City, street or address" value={f.location} onChange={set("location")} data-testid="claim-location-input" /></div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="c-desc">Describe what happened</Label><Textarea id="c-desc" rows={4} value={f.description} onChange={set("description")} data-testid="claim-description-input" /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="c-pr">Police / report # (optional)</Label><Input id="c-pr" value={f.police_report} onChange={set("police_report")} data-testid="claim-police-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-op">Other party (optional)</Label><Input id="c-op" placeholder="Name, insurer, plate" value={f.other_party} onChange={set("other_party")} data-testid="claim-other-party-input" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.injuries} onCheckedChange={(v) => setF({ ...f, injuries: !!v })} data-testid="claim-injuries-checkbox" /> Someone was injured</label>
          <Button disabled={!ok || busy} onClick={submit} className="bg-blue-700 text-white hover:bg-blue-800" data-testid="claim-submit-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit claim
          </Button>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
